import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  LayoutDashboard, 
  FileText, 
  Receipt, 
  HardHat,
  MapPin,
  CheckCircle
} from 'lucide-react';

const features = [
  {
    icon: LayoutDashboard,
    title: 'Pipeline Commercial',
    description: 'Suivez tes prospects du premier contact jusqu\'au chantier termine. Visualisez tout en un coup d\'oeil.',
    stages: ['Prospect', 'Devis envoye', 'Devis accepte', 'Chantier planifie', 'En cours', 'Termine'],
    color: 'bg-blue-50 text-blue-600 border-blue-100',
  },
  {
    icon: HardHat,
    title: 'Pipeline Chantier',
    description: 'Gerez l\'avancement de tes chantiers avec un tableau Kanban intuitif. Drag & drop pour mettre a jour.',
    stages: ['A planifier', 'Demarre', 'En cours', 'Termine'],
    color: 'bg-orange-50 text-ba-secondary border-orange-100',
  },
];

const crmFeatures = [
  {
    icon: FileText,
    title: 'Devis en quelques secondes',
    bullets: [
      'Creez tes devis rapidement',
      'Envoyez en PDF par email',
      'Signature electronique en ligne',
      'Transformation auto en facture',
    ],
    color: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  },
  {
    icon: Receipt,
    title: 'Facturation electronique',
    bullets: [
      'Facturation numerique conforme',
      'Suivi des paiements',
      'Relances automatiques',
      'Pret pour 2026',
    ],
    color: 'bg-purple-50 text-purple-600 border-purple-100',
  },
  {
    icon: HardHat,
    title: 'Gestion des chantiers',
    bullets: [
      'Description du travail',
      'Statut en temps reel',
      'Equipe assignee',
      'Temps passe et rentabilite',
    ],
    color: 'bg-teal-50 text-teal-600 border-teal-100',
  },
  {
    icon: MapPin,
    title: 'Geolocalisation chantier',
    bullets: [
      'Detection GPS automatique',
      'Demarrage auto du chantier',
      'Temps de travail enregistre',
      'Calcul de rentabilite precis',
    ],
    color: 'bg-rose-50 text-rose-600 border-rose-100',
  },
];

export default function CRMSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="section-premium bg-slate-50"
      data-testid="crm-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-flex items-center gap-2 badge-premium mb-6">
            <LayoutDashboard className="w-4 h-4" />
            Mon Cockpit
          </span>
          <h2 className="heading-section mb-6">
            Le cockpit de{' '}
            <span className="text-gradient">ton entreprise</span>
          </h2>
          <p className="body-large max-w-3xl mx-auto">
            Un Cockpit concu specifiquement pour les artisans du batiment. Tout ce dont tu as besoin pour gérer ton activité.
          </p>
        </motion.div>

        {/* Pipeline Cards */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-16 md:mb-20">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="card-premium bg-white rounded-3xl p-8 md:p-10"
              >
                <div className="flex items-start gap-5 mb-8">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${feature.color} border`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-slate-500">
                      {feature.description}
                    </p>
                  </div>
                </div>
                
                {/* Pipeline Preview */}
                <div className="flex flex-wrap gap-2">
                  {feature.stages.map((stage, stageIndex) => (
                    <motion.span 
                      key={stageIndex}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={isInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ delay: 0.3 + stageIndex * 0.05 }}
                      className="px-4 py-2 bg-slate-100 text-slate-600 text-sm font-medium rounded-full"
                    >
                      {stage}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {crmFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                className="card-premium bg-white rounded-2xl p-6 md:p-8"
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${feature.color} border`}>
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="heading-card mb-4">
                  {feature.title}
                </h3>
                <ul className="space-y-3">
                  {feature.bullets.map((bullet, bulletIndex) => (
                    <li key={bulletIndex} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-ba-success mt-0.5 flex-shrink-0" />
                      <span className="text-slate-500 text-sm">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Info Box - Facturation 2026 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 bg-gradient-to-r from-purple-50 to-blue-50 rounded-3xl p-8 md:p-10 border border-purple-100/50"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm border border-purple-100">
              <Receipt className="w-10 h-10 text-purple-600" />
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">
                Facturation electronique obligatoire en 2026
              </h3>
              <p className="text-slate-600 leading-relaxed">
                BatiAssist te permet d'anticiper cette transition. Tes factures sont déjà conformes aux nouvelles normes. 
                Suivi des paiements, relances automatiques, archivage numérique : tout est prêt.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
